import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTripStoreUpdater } from '../../hooks';
import {
    Banner,
    EarlyCheckoutModal,
    Loading,
    ManageTrip,
    QuestionPanel,
    RequiredActionsModal,
    StayPanel,
    TextBox,
    UnitPanel,
} from '../../components';
import moment from 'moment';
import _ from 'lodash';
import { StoreState } from '../../types/store.type';
import { CookieKeys } from '../../types/keys.type';
import { Splits } from '../../types/split.type';
import { SharedUtils, TranslationUtils } from '../../utils';
import { StarRatingModal } from '../../components/StarRatingModal/StarRatingModal';
import { useFeatureConfig } from '../../services/optimizely/optimizelyHelper';

export const Reservation: React.FC = () => {
    useTripStoreUpdater();

    const trip = useSelector((store: StoreState) => store.trip);

    const [isShowingActionsModal, setIsShowingActionsModal] = useState(false);
    const [isShowingAppBanner, setIsShowingAppBanner] = useState(true);
    const [isStarRatingModalShow, setIsStarRatingModalShow] = useState(false);
    const [isActionsCompeted, setIsActionsCompleted] = useState<boolean>(false);

    // Feature Flag
    const { isEnabled: isAppBannerEnable } = useFeatureConfig(Splits.GUESTS_PORTAL_MOBILE_APP, false);

    const requiredActions = trip.required_actions;
    const { reservation, stay, unit, review } = trip;

    useEffect(() => {
        if (reservation && stay) {
            setIsShowingActionsModal(() => {
                const modalDismissCookie = SharedUtils.getCookie(CookieKeys.MODAL_DISMISSED);

                const isFutureReservation = moment(stay.check_in_date_time).isAfter(moment(new Date()));

                const actionsCompleted = _.reduce(requiredActions, (acc, value) => acc && value !== undefined && value.completed, true);

                setIsActionsCompleted(actionsCompleted);

                const isModalDismissed = modalDismissCookie === reservation.id;

                return isFutureReservation && !actionsCompleted && !isModalDismissed;
            });
        }
    }, [requiredActions]);

    useEffect(() => {
        if (review && review.is_eligible && review.rating === null) {
            setIsStarRatingModalShow(true);
        }
    }, [review]);

    if (trip.isLoading) {
        return <Loading />;
    }

    if (!reservation || !stay || !unit) {
        return null;
    }

    const handleModalDismiss = () => {
        const today = moment(new Date());
        const checkinDate = moment(new Date(stay.check_in_date_time));

        const diff = checkinDate.diff(today, 'days');

        let expires;

        if (diff <= 7) {
            expires = today.add(1, 'days').toDate();
        } else if (diff <= 30) {
            expires = checkinDate.subtract(1, 'week').toDate();
        } else {
            expires = checkinDate.subtract(1, 'month').toDate();
        }

        SharedUtils.setCookie(CookieKeys.MODAL_DISMISSED, reservation.id, { expires });

        setIsShowingActionsModal(false);
    };

    const handleAppBannerDismiss = () => {
        setIsShowingAppBanner(false);
    };

    const handleStarRatingModalDismiss = () => {
        setIsStarRatingModalShow(false);
    };

    return (
        <React.Fragment>
            <div>
                <div className="pl-0">{isAppBannerEnable && <Banner show={isShowingAppBanner} handleClose={handleAppBannerDismiss} />}</div>
                <h1 tabIndex={0} className="heading-large page-title">
                    {TranslationUtils.get('trip_overview')}
                </h1>

                <UnitPanel />

                <StayPanel />

                <div>
                    <span tabIndex={0} className="heading-caps-small head-title my-3">
                        {isActionsCompeted ? TranslationUtils.get('trip_all_set') : TranslationUtils.get('finalize_trip')}
                    </span>
                    <ManageTrip />
                </div>
                <div>
                    <div className={'mb-4'} />
                    {unit.important_info && (
                        <div tabIndex={0}>
                            {/*TODO:verify this*/}
                            <h2 className="page-subtitle">{TranslationUtils.get('know_before_you_go')}</h2>
                            <div className={'mb-2'} />
                            <TextBox
                                classTitle={'page-subtitle-notes'}
                                classNote={'page-text-notes'}
                                inline={true}
                                text={unit.important_info}
                                maxLength={1000}
                            />
                            <div className={'mb-5'} />
                        </div>
                    )}
                </div>

                <QuestionPanel />
            </div>

            <RequiredActionsModal
                show={isShowingActionsModal}
                handleClose={() => {
                    handleModalDismiss();
                }}
            />

            <EarlyCheckoutModal reservation={reservation} stay={stay} />

            {review && (
                <StarRatingModal
                    reservation={reservation}
                    unit={unit}
                    review={review}
                    show={isStarRatingModalShow}
                    handleClose={handleStarRatingModalDismiss}
                />
            )}
        </React.Fragment>
    );
};
